import React from 'react';
import { Calendar, FileText, Trash2, Edit2, Users, Eye, Copy, ChevronDown } from 'lucide-react';
import { Database } from '../types/supabase';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import TaskDetails from './TaskDetails';
import { toast } from '../lib/toast';

type Task = Database['public']['Tables']['tasks']['Row'];
type TeamMember = Database['public']['Tables']['team_members']['Row'];

interface TaskListProps {
  tasks: Task[];
  navigate: (to: string) => void;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onToggleComplete: (taskId: string, completed: boolean) => void;
  onUpdateStatus?: (taskId: string, status: string) => void;
  onUpdatePriority?: (taskId: string, priority: string) => void;
  onDuplicate?: (task: Task) => void;
}

export default function TaskList({
  tasks,
  navigate,
  onEdit,
  onDelete,
  onToggleComplete,
  onUpdateStatus,
  onUpdatePriority,
  onDuplicate,
}: TaskListProps) {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskMembers, setTaskMembers] = useState<Record<string, TeamMember[]>>({});
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [activeMenuType, setActiveMenuType] = useState<'status' | 'priority' | null>(null);

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        if (!tasks.length) return;

        const { data, error } = await supabase
          .from('task_assignments')
          .select(`
            task_id,
            team_member_id,
            team_members(*)
          `)
          .in('task_id', tasks.map(t => t.id));

        if (error) throw error;

        const membersByTask: Record<string, TeamMember[]> = {};
        data.forEach((assignment: any) => {
          if (!membersByTask[assignment.task_id]) {
            membersByTask[assignment.task_id] = [];
          }
          if (assignment.team_members) {
            membersByTask[assignment.task_id].push(assignment.team_members);
          }
        });

        setTaskMembers(membersByTask);
      } catch (error) {
        console.error('Error fetching assignments:', error);
        // Don't show error toast to users, just log it
        console.warn('Team members may be unavailable');
      }
    };

    if (tasks.length > 0) {
      fetchAssignments();
    }
  }, [tasks]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'Normal':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
      case 'High':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200';
      case 'Urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Not Started':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
      case 'Testing':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200';
      case 'Awaiting Feedback':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200';
      case 'Completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const statusOptions = [
    'Not Started',
    'In Progress',
    'Testing',
    'Awaiting Feedback',
    'Completed'
  ];

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    if (onUpdateStatus) {
      await onUpdateStatus(taskId, newStatus);
      if (newStatus === 'Completed') {
        onToggleComplete(taskId, true);
      } else if (tasks.find(t => t.id === taskId)?.completed) {
        onToggleComplete(taskId, false);
      }
    }
    closeMenus();
  };

  const handlePriorityChange = async (taskId: string, newPriority: string) => {
    if (onUpdatePriority) {
      await onUpdatePriority(taskId, newPriority);
    }
    closeMenus();
  };

  const toggleMenu = (taskId: string, menuType: 'status' | 'priority') => {
    if (activeMenuId === taskId && activeMenuType === menuType) {
      closeMenus();
    } else {
      setActiveMenuId(taskId);
      setActiveMenuType(menuType);
    }
  };

  const closeMenus = () => {
    setActiveMenuId(null);
    setActiveMenuType(null);
  };

  const truncateHTML = (html: string, maxLength: number = 150) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length <= maxLength ? html : text.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className="space-y-4">
      {tasks.map((task) => (
        <div
          key={task.id}
          className={`bg-white rounded-lg shadow p-4 sm:p-6 transition-opacity ${
            task.completed ? 'opacity-50' : ''
          }`}
        >
          <div className="flex flex-col space-y-4">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                checked={task.completed}
                onChange={(e) => onToggleComplete(task.id, e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3
                  className={`text-lg font-medium line-clamp-2 ${
                    task.completed 
                      ? 'line-through text-gray-500 dark:text-gray-400'
                      : 'text-gray-900 dark:text-white'
                  }`}
                  onClick={() => navigate(`/tasks/${task.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {task.title}
                </h3>
                {task.description && (
                  <div className="mt-1 group">
                    <div
                      className="text-gray-500 prose prose-sm dark:prose-invert"
                      dangerouslySetInnerHTML={{ __html: truncateHTML(task.description, 100) }}
                    />
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                    <div className="relative">
                      <button
                        onClick={() => toggleMenu(task.id, 'priority')}
                        className={`inline-flex items-center h-7 px-3 whitespace-nowrap rounded-md text-sm font-medium ${getPriorityColor(task.priority)}`}
                      >
                        Priority: {task.priority}
                      </button>
                      {activeMenuId === task.id && activeMenuType === 'priority' && (
                        <div className="absolute left-0 mt-2 w-40 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10">
                          <div className="py-1">
                            {['Low', 'Normal', 'High', 'Urgent'].map((priority) => (
                              <button
                                key={`${task.id}-${priority}`}
                                onClick={() => {
                                  handlePriorityChange(task.id, priority);
                                }}
                                className={`block w-full text-left px-4 py-2 text-sm ${
                                  task.priority === priority
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                              >
                                {priority}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <button
                        onClick={() => toggleMenu(task.id, 'status')}
                        className={`inline-flex items-center h-7 px-3 whitespace-nowrap rounded-md text-sm font-medium ${getStatusColor(task.status)}`}
                      >
                        Status: {task.status}
                      </button>
                      {activeMenuId === task.id && activeMenuType === 'status' && (
                        <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10">
                          <div className="py-1">
                            {statusOptions.map((status) => (
                              <button
                                key={`${task.id}-${status}`}
                                onClick={() => {
                                  handleStatusChange(task.id, status);
                                  closeMenus();
                                }}
                                className={`block w-full text-left px-4 py-2 text-sm ${
                                  task.status === status
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                              >
                                {status}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  {task.due_date && (
                    <span className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(task.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {task.attachment_url && (
                    <a
                      href={task.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-indigo-600 hover:text-indigo-500"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      {task.attachment_name || 'Attachment'}
                    </a>
                  )}
                  {taskMembers[task.id]?.length > 0 && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="h-4 w-4 mr-1" />
                      <span>
                        {taskMembers[task.id].map(m => m.name).join(', ')}
                      </span>
                    </div>
                  )}
              </div>
            </div>
            {/* Action buttons */}
            <div className="flex items-center justify-end space-x-3 mt-2">
              <button
                onClick={() => navigate(`/tasks/${task.id}`)}
                className="p-1.5 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100"
              >
                <Eye className="h-5 w-5" aria-label="View task" />
              </button>
              <button
                onClick={() => onDuplicate?.(task)}
                className="p-1.5 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100"
                title="Duplicate task"
              >
                <Copy className="h-5 w-5" aria-label="Duplicate task" />
              </button>
              <button
                onClick={() => onEdit(task)}
                className="p-1.5 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100"
              >
                <Edit2 className="h-5 w-5" aria-label="Edit task" />
              </button>
              <button
                onClick={() => onDelete(task.id)}
                className="p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100"
              >
                <Trash2 className="h-5 w-5" aria-label="Delete task" />
              </button>
            </div>
          </div>
          </div>
        </div>
      ))}
      {selectedTask && (
        <TaskDetails
          task={selectedTask}
          teamMembers={taskMembers[selectedTask.id] || []}
          onClose={() => setSelectedTask(null)}
        />
      )}
      {tasks.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No tasks found. Create one to get started!</p>
        </div>
      )}
    </div>
  );
}